/* =============================================================
   dd-animated-underlines.js

   Walk the DOM, find every element whose *computed* style has an
   underline, and tag it with `data-dd-animated-underline` so the
   companion CSS can replace that underline with an animated
   left-to-right reveal.

   Why runtime detection instead of editing ~30 stylesheets?
   - Every theme touchpoint (PDP, cart drawer, footer, facets,
     predictive search, Liquid accordions, etc.) sets
     `text-decoration: underline` in its own scope.
   - A computed-style sweep catches all of them in one place —
     including dynamically-injected HTML (cart drawer renders,
     predictive search results, etc.) via a MutationObserver.
   - Zero risk of regressing existing layout/spacing. We don't
     touch any existing rules; we just add a tag + override.

   Two detection passes:
   1. Load-time sweep for statically-underlined elements.
   2. Live hover probe (pointerover delegation) for
      `:hover`-triggered underlines — these don't exist at load
      time so the first pass misses them. On first hover we check
      computed style (which now reflects the active `:hover`
      rule), tag, and momentarily force the `0%` start state so
      the transition actually plays on this first-hover too.

   Not limited to <a> — review strips and card titles apply
   underlines to child <span>s, so we tag any element whose
   computed style ends up underlined.

   Skips:
   - Hover-incapable devices (the CSS media query already handles
     this, but we also bail on scanning to save cycles).
   - Elements that opt out via `data-dd-underline="static"`.
   - Elements without visible text (SVGs, wrappers that just
     happen to inherit the property).
   ============================================================= */

(function () {
  'use strict';

  // Bail entirely on no-hover devices — the native underline is
  // the right affordance there and there's no mouse cursor to
  // trigger the animation anyway.
  var canHover =
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!canHover) return;

  var ATTR = 'data-dd-animated-underline';
  var OPT_OUT_ATTR = 'data-dd-underline';

  // Elements we never want to animate even if they inherit
  // `text-decoration: underline` — they're structural or non-text.
  var SKIP_TAGS = {
    SVG: 1,
    PATH: 1,
    IMG: 1,
    BR: 1,
    HR: 1,
    SCRIPT: 1,
    STYLE: 1,
    NOSCRIPT: 1,
    INPUT: 1,
    TEXTAREA: 1,
    SELECT: 1,
    BUTTON: 1,
  };

  function hasUnderline(el) {
    var cs;
    try {
      cs = window.getComputedStyle(el);
    } catch (err) {
      return false;
    }
    if (!cs) return false;

    // `text-decoration-line` is the modern property; fall back to
    // the composite `text-decoration` string for older engines.
    var line = cs.textDecorationLine || '';
    if (line && /underline/i.test(line)) return true;

    var shorthand = cs.textDecoration || '';
    return /underline/i.test(shorthand);
  }

  function hasVisibleText(el) {
    // Guard against tagging wrappers/empty elements that inherit
    // the decoration but render no glyphs — the animated gradient
    // would paint a lone bar with nothing above it.
    var text = (el.textContent || '').replace(/\s+/g, '');
    return text.length > 0;
  }

  function shouldTag(el) {
    if (!el || el.nodeType !== 1) return false;
    if (SKIP_TAGS[el.tagName]) return false;
    if (el.hasAttribute(ATTR)) return false;
    if (el.getAttribute(OPT_OUT_ATTR) === 'static') return false;
    if (!hasVisibleText(el)) return false;
    return hasUnderline(el);
  }

  function tag(el) {
    el.setAttribute(ATTR, '');
  }

  /**
   * Tag an element that's *currently* being hovered and force the
   * reveal to start from 0% on this very hover. Without the
   * reflow-and-release dance, the browser would jump straight to
   * 100% because the `:hover` selector matches the moment we add
   * the attribute.
   */
  function tagAndAnimate(el) {
    el.setAttribute(ATTR, '');
    // Pin the starting state inline…
    el.style.backgroundSize = '0% 1.5px';
    // …force a reflow so the browser commits it…
    void el.offsetWidth;
    // …then clear the inline value so the `:hover` rule wins and
    // transitions into `100% 1.5px` naturally.
    el.style.backgroundSize = '';
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    // Broad net: any element that could carry text content. We
    // still filter in `shouldTag` so this stays cheap in practice
    // — the underline check is a single getComputedStyle read per
    // candidate.
    var candidates = scope.querySelectorAll(
      'a, span, strong, em, u, li, p, small, b, i, ins, mark, button, label, h1, h2, h3, h4, h5, h6'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (shouldTag(candidates[i])) tag(candidates[i]);
    }
  }

  /**
   * pointerover bubbles, so a single document-level listener
   * catches every hover across the theme. We walk up from the
   * event target checking each ancestor — a hover that applies
   * underline to a child span still reports the span (or a
   * deeper descendant) as `e.target` once the cursor is over it.
   */
  function onPointerOver(e) {
    var node = e.target;
    if (!node || node.nodeType !== 1) return;

    // Cap the walk at 6 levels so we don't wander up into
    // <html> on every mousemove burst.
    var depth = 0;
    while (node && node !== document.body && depth < 6) {
      if (node.nodeType === 1 && shouldTag(node)) {
        tagAndAnimate(node);
        return;
      }
      node = node.parentNode;
      depth++;
    }
  }

  function init() {
    scan(document);

    // Live hover probe — catches `:hover`-only underlines that
    // the load-time sweep can't see (the rule doesn't apply
    // until hover, so computed style shows no underline yet).
    document.addEventListener('pointerover', onPointerOver, true);

    // Observe for dynamic content — cart drawer, AJAX sections,
    // predictive search results, accordion expansions, etc.
    // Debounced via rAF so a burst of mutations (common during
    // cart renders) only triggers one scan per frame.
    if (!('MutationObserver' in window)) return;

    var pending = false;
    var queue = [];

    function flush() {
      pending = false;
      var items = queue;
      queue = [];

      for (var i = 0; i < items.length; i++) {
        var node = items[i];
        if (!node || node.nodeType !== 1) continue;
        if (shouldTag(node)) tag(node);
        if (node.querySelectorAll) scan(node);
      }
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          queue.push(added[j]);
        }
      }
      if (!pending && queue.length) {
        pending = true;
        requestAnimationFrame(flush);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
